import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, GitBranch, ChevronDown, ChevronRight, Clock, ArrowUpRight, ArrowDownRight, Minus, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function statusColor(status) {
  if (status === "completed") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (status === "needs_review") return "bg-amber-100 text-amber-700 border-amber-200";
  if (status === "failed") return "bg-red-100 text-red-700 border-red-200";
  return "bg-gray-100 text-gray-600 border-gray-200";
}

function ChangeBadge({ change }) {
  const isPositive = change.includes("+") || change.includes("updated");
  const isNegative = change.includes("-") && !change.includes("->");
  const Icon = isPositive ? ArrowUpRight : isNegative ? ArrowDownRight : Minus;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#f8f6f1] border border-[#ede8dc] text-[#555]">
      <Icon className="h-2.5 w-2.5" />
      {change}
    </span>
  );
}

function VersionRow({ version, isLatest }) {
  const [expanded, setExpanded] = useState(false);
  const output = version.primary_outputs || {};

  return (
    <div
      className={`border rounded-xl transition-all ${isLatest ? "border-emerald-200 bg-emerald-50/30" : "border-[#ede8dc] bg-white"}`}
      data-testid={`version-row-${version.version}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        data-testid={`version-toggle-${version.version}`}
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5 text-[#999] shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-[#999] shrink-0" />}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-xs font-bold text-[#0A0A0A]">v{version.version}</span>
          {isLatest && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">LATEST</span>}
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${statusColor(version.status)}`}>{version.status}</span>
          {version.confidence_score != null && (
            <span className="text-[10px] text-[#888] font-medium tabular-nums">Score: {version.confidence_score}</span>
          )}
        </div>
        <span className="text-[10px] text-[#aaa] whitespace-nowrap flex items-center gap-1">
          <Clock className="h-2.5 w-2.5" />
          {version.started_at ? new Date(version.started_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#f0ece4]">
          {/* Changes from previous */}
          {version.changes_from_previous && version.changes_from_previous.length > 0 && (
            <div className="pt-3">
              <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-2">Changes from v{version.version - 1}</div>
              <div className="flex flex-wrap gap-1.5">
                {version.changes_from_previous.map((c, i) => <ChangeBadge key={i} change={c} />)}
              </div>
            </div>
          )}

          {/* Summary */}
          {version.summary && (
            <div className="pt-2">
              <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-1">Summary</div>
              <p className="text-xs text-[#333] leading-relaxed">{version.summary}</p>
            </div>
          )}

          {/* Key outputs */}
          {Object.keys(output).length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-2">Key Outputs</div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(output).map(([key, val]) => {
                  if (Array.isArray(val) || typeof val === "object") return null;
                  return (
                    <div key={key} className="flex justify-between items-center px-2.5 py-1.5 rounded-lg bg-[#fdfcf9] border border-[#ede8dc]">
                      <span className="text-[10px] text-[#888]">{key.replace(/_/g, " ")}</span>
                      <span className="text-[10px] font-bold text-[#333] tabular-nums">{typeof val === "number" ? (val % 1 === 0 ? val : val.toFixed(1)) : String(val)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recommended actions */}
          {version.recommended_actions && version.recommended_actions.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-2">Recommended Actions</div>
              <div className="space-y-1">
                {version.recommended_actions.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-[#555]">
                    <span className={`h-1.5 w-1.5 rounded-full ${a.priority === "high" ? "bg-red-400" : a.priority === "medium" ? "bg-amber-400" : "bg-emerald-400"}`} />
                    {a.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgentVersionHistory({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/agents/deal/${leadId}/version-history`, { headers })
      .then(r => {
        setData(r.data);
        if (r.data.agents?.length > 0) setExpandedAgent(r.data.agents[0].agent_name);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;
  if (!data || !data.agents?.length) return <p className="text-sm text-[#aaa] text-center py-6">No agent runs recorded for this deal</p>;

  return (
    <div className="space-y-4" data-testid="agent-version-history">
      <div className="flex items-center gap-2 mb-1">
        <GitBranch className="h-4 w-4 text-emerald-700" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Agent Version History</span>
        <span className="text-[10px] text-[#bbb]">{data.total_agents} agents, {data.agents.reduce((s, a) => s + a.total_versions, 0)} total runs</span>
      </div>

      {data.agents.map(agent => {
        const isOpen = expandedAgent === agent.agent_name;
        return (
          <div key={agent.agent_name} className="rounded-2xl border border-[#ede8dc] bg-white overflow-hidden" data-testid={`agent-group-${agent.agent_name}`}>
            <button
              onClick={() => setExpandedAgent(isOpen ? null : agent.agent_name)}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[#fdfcf9] transition-colors"
              data-testid={`agent-group-toggle-${agent.agent_name}`}
            >
              {isOpen ? <ChevronDown className="h-4 w-4 text-emerald-600" /> : <ChevronRight className="h-4 w-4 text-[#bbb]" />}
              <div className="flex-1 text-left">
                <span className="text-sm font-bold text-[#0A0A0A]">{agent.label}</span>
                <span className="text-[10px] text-[#aaa] ml-2">{agent.total_versions} version{agent.total_versions !== 1 ? "s" : ""}</span>
              </div>
              {agent.latest_version && (
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${statusColor(agent.latest_version.status)}`}>
                    {agent.latest_version.status}
                  </span>
                  {agent.latest_version.confidence_score != null && (
                    <span className="text-xs font-bold text-[#333] tabular-nums">{agent.latest_version.confidence_score}%</span>
                  )}
                </div>
              )}
            </button>

            {isOpen && (
              <div className="px-5 pb-4 space-y-2 border-t border-[#f0ece4]">
                <div className="pt-3" />
                {agent.versions.map((v, i) => (
                  <VersionRow key={v.run_id} version={v} isLatest={i === 0} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
